/**
 * LoginPage — Landing page with sign-in button
 *
 * Direct clone of landing-v5.html with a "Sign in with Microsoft"
 * button added below the LDAJA logo.
 *
 * Design: Maya Rodriguez (UI/UX Architect)
 * Implementation: Marcus Chen (Senior Full-Stack Developer)
 */

interface LoginPageProps {
  onLogin: () => void;
  isLoading: boolean;
}

function MicrosoftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

export function LoginPage({ onLogin, isLoading }: LoginPageProps) {
  return (
    <div style={{ margin: 0, padding: 0, background: '#000', minHeight: '100vh', overflow: 'hidden' }}>
      {/* Mars background — same as landing-v5.html .bg-layer */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          background: "url('/mars-bg.jpg') center center / cover no-repeat",
        }}
      >
        {/* Gradient overlay — same as landing-v5.html .bg-layer::after */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0.85) 100%)',
          }}
        />
      </div>

      {/* Logo — positioned exactly as landing-v5.html .content */}
      <div
        style={{
          position: 'fixed',
          top: '43%',
          left: '50%',
          transform: 'translate(-50%, calc(-50% - 30px))',
          zIndex: 2,
        }}
      >
        <img
          src="/ldaja-logo-with-tagline.png"
          alt="LDAJA — Build Something Better"
          className="animate-rise-in"
          style={{
            width: 'clamp(220px, 38vw, 650px)',
            height: 'auto',
            display: 'block',
            filter: 'drop-shadow(0 4px 30px rgba(37, 99, 235, 0.3))',
            animationDelay: '0.2s',
          }}
        />
      </div>

      {/* Sign-in area — positioned below the logo */}
      <div
        style={{
          position: 'fixed',
          top: '60%',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <button
          onClick={onLogin}
          disabled={isLoading}
          className="animate-fade-in"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            padding: '12px 32px',
            background: isLoading ? 'rgba(37, 99, 235, 0.6)' : '#2563EB',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: 600,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'all 200ms ease-out',
            boxShadow: isLoading ? 'none' : '0 4px 24px rgba(37, 99, 235, 0.35)',
            animationDelay: '1s',
            opacity: 0,
            animationFillMode: 'forwards',
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.currentTarget.style.background = '#1D4ED8';
              e.currentTarget.style.boxShadow = '0 6px 32px rgba(37, 99, 235, 0.5)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.currentTarget.style.background = '#2563EB';
              e.currentTarget.style.boxShadow = '0 4px 24px rgba(37, 99, 235, 0.35)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          {isLoading ? <Spinner /> : <MicrosoftIcon />}
          <span>{isLoading ? 'Signing in...' : 'Sign in with Microsoft'}</span>
        </button>

        <p
          className="animate-fade-in"
          style={{
            marginTop: '20px',
            fontSize: '0.75rem',
            color: 'rgba(255, 255, 255, 0.25)',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            animationDelay: '1.2s',
            opacity: 0,
            animationFillMode: 'forwards',
          }}
        >
          Authorized personnel only. Access is logged.
        </p>
      </div>

      {/* Footer — same as landing-v5.html */}
      <footer
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 2,
          textAlign: 'center',
          padding: '24px',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '0.8rem',
          color: 'rgba(255, 255, 255, 0.3)',
        }}
      >
        &copy; 2026 LDAJA. All rights reserved.
      </footer>
    </div>
  );
}
